if (!customElements.get('cart-drawer-sample-gift')) {
  const PICKER_OPEN_KEY = 'cart-drawer-sample-gift-picker-open';
  const CAROUSEL_REVEALED_KEY = 'cart-drawer-sample-gift-carousel-revealed';
  const UNLOCK_ANIMATION_MS = 1200;
  const { getFreeSampleState, isGiftLine } = window.HugyFreeSampleState || {};

  const unlockSequence = {
    pending: false,
    revealUntil: 0,
    revealed: false,
    reset() {
      this.pending = false;
      this.revealUntil = 0;
      this.revealed = false;
      sessionStorage.removeItem(CAROUSEL_REVEALED_KEY);
    },
    markRevealed() {
      this.revealed = true;
      this.pending = false;
      this.revealUntil = 0;
      sessionStorage.setItem(CAROUSEL_REVEALED_KEY, '1');
    },
    wasRevealed() {
      return this.revealed || sessionStorage.getItem(CAROUSEL_REVEALED_KEY) === '1';
    }
  };

  class CartDrawerSampleGift extends HTMLElement {
    constructor() {
      super();
      this.isProcessing = false;
      this.swiper = null;
      this.carouselRevealTimer = null;
    }

    connectedCallback() {
      this.noticeEl = this.querySelector('[data-sample-gift-notice]');
      this.abortController = new AbortController();
      const { signal } = this.abortController;

      this.addEventListener('click', this.handleClick);

      document.addEventListener('on:cart:add', (e) => this.onCartChange(e.detail?.cart), { signal });
      document.addEventListener('on:line-item:change', (e) => this.onCartChange(e.detail?.cart), { signal });
      document.addEventListener('on:sample-gift:mount', () => this.initCarousel(), { signal });
      document.addEventListener('on:progress-bar:final-milestone-complete', () => this.onProgressBarHidden(), { signal });
      document.addEventListener('on:progress-bar:final-milestone-reset', () => this.onProgressBarReset(), { signal });

      const shouldOpenPicker = sessionStorage.getItem(PICKER_OPEN_KEY) === '1';
      if (shouldOpenPicker) {
        sessionStorage.removeItem(PICKER_OPEN_KEY);
        this.classList.add('cart-drawer-sample-gift--picker-open');
      }

      if (unlockSequence.wasRevealed()) {
        unlockSequence.revealed = true;
        const picker = this.querySelector('[data-sample-gift-picker]');
        const heading = this.querySelector('[data-sample-gift-heading]');
        if (picker && this.dataset.sampleEligible === 'true' && this.dataset.freeApplied !== 'true') {
          picker.hidden = false;
          picker.classList.remove('is-revealing');
          if (heading) heading.hidden = false;
          requestAnimationFrame(() => this.initCarousel());
        }
      }

      this.onCartChange();
      requestAnimationFrame(() => this.initCarousel());
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      if (this.carouselRevealTimer) clearTimeout(this.carouselRevealTimer);
      this.destroyCarousel();
      this.abortController?.abort();
    }

    destroyCarousel() {
      if (!this.swiper) return;
      this.swiper.destroy(true, true);
      this.swiper = null;
      const root = this.querySelector('[data-carousel-root]');
      if (root) root.classList.remove('is-ready');
    }

    initCarousel() {
      const root = this.querySelector('[data-carousel-root]');
      const picker = this.querySelector('[data-sample-gift-picker]');
      if (!root || !picker || picker.hidden) return;
      if (typeof Swiper === 'undefined') return;

      const slideCount = root.querySelectorAll('.swiper-slide').length;
      if (slideCount === 0) return;

      if (this.swiper) {
        this.swiper.update();
        root.classList.add('is-ready');
        return;
      }

      this.swiper = new Swiper(root, {
        slidesPerView: 3,
        spaceBetween: 8,
        slidesPerGroup: 1,
        watchOverflow: true,
        observer: true,
        observeParents: true,
        navigation: {
          nextEl: this.querySelector('[data-carousel-next]'),
          prevEl: this.querySelector('[data-carousel-prev]')
        },
        on: {
          init: (swiper) => {
            root.classList.add('is-ready');
            swiper.navigation?.update?.();
          },
          resize: (swiper) => {
            swiper.update();
          }
        }
      });
    }

    revealPicker(pickerEl) {
      if (!pickerEl) return;

      if (!pickerEl.hidden) {
        this.initCarousel();
        return;
      }

      pickerEl.hidden = false;
      pickerEl.classList.remove('is-revealing');
      this.initCarousel();
    }

    handleClick = (e) => {
      if (e.target.closest('.js-sample-gift-change')) {
        e.preventDefault();
        this.handleChangeClick();
        return;
      }

      const btn = e.target.closest('.js-sample-gift-add');
      if (btn) this.handleAddClick(btn);
    };

    getState(cart) {
      if (!getFreeSampleState) return null;
      return getFreeSampleState(cart, {
        thresholdCents: Number(this.dataset.sampleThresholdCents) || 12000,
        sampleProductIds: this.dataset.sampleCollectionProductIds || ''
      });
    }

    cartJsUrl() {
      const base = window.theme?.routes?.cart || '/cart';
      return /\.js(\?|$)/.test(base) ? base : `${base}.js`;
    }

    cartAddUrl() {
      return window.theme?.routes?.cartAdd || '/cart/add.js';
    }

    cartChangeUrl() {
      return window.theme?.routes?.cartChange || '/cart/change.js';
    }

    getSectionsParam() {
      const cartDrawer = document.getElementById('cart-drawer');
      const sections = ['cart-icon-bubble'];
      if (cartDrawer) {
        const sectionId = cartDrawer.closest('.shopify-section')?.id?.replace('shopify-section-', '');
        if (sectionId) sections.push(sectionId);
      }
      return sections.join(',');
    }

    async fetchCart() {
      const res = await fetch(this.cartJsUrl(), {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error(`cart.js HTTP ${res.status}`);
      return res.json();
    }

    showNotice(message) {
      if (!this.noticeEl || !message) return;
      this.noticeEl.textContent = message;
      this.noticeEl.classList.remove('hidden');
    }

    hideNotice() {
      if (!this.noticeEl) return;
      this.noticeEl.textContent = '';
      this.noticeEl.classList.add('hidden');
    }

    closePicker() {
      this.classList.remove('cart-drawer-sample-gift--picker-open');
    }

    onProgressBarHidden() {
      if (!unlockSequence.pending) return;

      unlockSequence.pending = false;
      unlockSequence.revealUntil = Date.now() + UNLOCK_ANIMATION_MS;
      this.syncUI(this.lastCart);
    }

    onProgressBarReset() {
      unlockSequence.reset();
      if (this.carouselRevealTimer) {
        clearTimeout(this.carouselRevealTimer);
        this.carouselRevealTimer = null;
      }
      this.restoreUnlockMessage(this.querySelector('[data-sample-gift-unlock-message]'));
      this.syncUI(this.lastCart);
    }

    getUnlockState(showCarousel, pickerOpen, state, wasSampleEligible) {
      if (!state.isThresholdReached || state.freeApplied) {
        if (!state.isThresholdReached) {
          unlockSequence.reset();
        }
      }

      if (!showCarousel || pickerOpen || state.freeApplied) {
        return { delay: 0, showMessage: false, waitForProgressBar: false };
      }

      if (
        (wasSampleEligible || unlockSequence.wasRevealed()) &&
        state.isThresholdReached &&
        state.canAddFreeSample
      ) {
        return { delay: 0, showMessage: false, waitForProgressBar: false };
      }

      const now = Date.now();

      if (!wasSampleEligible && state.isThresholdReached && state.canAddFreeSample) {
        const progressBarVisible = document.querySelector(
          '.cart-drawer__delivery-progress .custom-progress-bar--drawer:not(.hidden)'
        );

        if (progressBarVisible) {
          unlockSequence.pending = true;
          const progressDelay = window.HugyCustomProgressBar?.getDrawerCompletionDelay?.() || 0;
          return { delay: progressDelay, showMessage: false, waitForProgressBar: true };
        }

        unlockSequence.revealUntil = now + UNLOCK_ANIMATION_MS;
        return { delay: UNLOCK_ANIMATION_MS, showMessage: true, waitForProgressBar: false };
      }

      if (unlockSequence.pending) {
        const progressDelay = window.HugyCustomProgressBar?.getDrawerCompletionDelay?.() || 0;
        return { delay: progressDelay, showMessage: false, waitForProgressBar: true };
      }

      if (unlockSequence.revealUntil > now) {
        return { delay: unlockSequence.revealUntil - now, showMessage: true, waitForProgressBar: false };
      }

      return { delay: 0, showMessage: false, waitForProgressBar: false };
    }

    restoreHeading(headingEl) {
      if (!headingEl) return;

      headingEl.classList.remove('is-unlocking');
      headingEl.querySelectorAll('.cart-drawer-sample-gift__confetti').forEach((el) => el.remove());
      headingEl.textContent = headingEl.dataset.sampleGiftDefaultHeading || headingEl.textContent;
    }

    restoreUnlockMessage(unlockEl) {
      if (!unlockEl) return;

      unlockEl.classList.remove('is-unlocking');
      unlockEl.querySelectorAll('.cart-drawer-sample-gift__confetti').forEach((el) => el.remove());
      unlockEl.hidden = true;
    }

    renderUnlockMessage(unlockEl) {
      if (!unlockEl) return;

      unlockEl.hidden = false;

      if (unlockEl.classList.contains('is-unlocking')) return;

      unlockEl.classList.add('is-unlocking');

      unlockEl.addEventListener(
        'animationend',
        () => unlockEl.classList.remove('is-unlocking'),
        { once: true }
      );

      const colors = ['#ffef53', '#093027', '#c0925d', '#ffd800', '#ffffff'];
      for (let i = 0; i < 12; i += 1) {
        const particle = document.createElement('span');
        particle.className = 'cart-drawer-sample-gift__confetti';
        particle.setAttribute('aria-hidden', 'true');
        particle.style.setProperty('--sample-confetti-x', ((Math.random() - 0.5) * 86).toFixed(1) + 'px');
        particle.style.setProperty('--sample-confetti-y', (-12 - Math.random() * 42).toFixed(1) + 'px');
        particle.style.setProperty('--sample-confetti-r', (Math.random() * 520 - 260).toFixed(0) + 'deg');
        particle.style.setProperty('--sample-confetti-color', colors[i % colors.length]);
        if (Math.random() > 0.5) particle.style.borderRadius = '50%';
        particle.addEventListener('animationend', () => particle.remove(), { once: true });
        unlockEl.append(particle);
      }
    }

    syncUI(cart) {
      const state = this.getState(cart);
      if (!state) return;

      this.lastCart = cart;

      const pickerOpen = this.classList.contains('cart-drawer-sample-gift--picker-open');
      const hadFreeApplied = this.dataset.freeApplied === 'true';
      const wasSampleEligible = this.dataset.sampleEligible === 'true';

      this.dataset.paidSubtotalCents = String(state.paidSubtotal);
      this.dataset.freeApplied = state.freeApplied ? 'true' : 'false';
      this.dataset.sampleEligible = state.isThresholdReached ? 'true' : 'false';

      this.classList.toggle('cart-drawer-sample-gift--locked', state.shouldDisableCarousel);
      this.classList.toggle('hidden', !cart?.item_count);

      if (!cart?.item_count) {
        unlockSequence.reset();
      }

      const showCarousel = state.shouldShowCarousel || pickerOpen;
      const showChange = state.shouldShowChange && !pickerOpen;
      const unlockState = this.getUnlockState(showCarousel, pickerOpen, state, wasSampleEligible);
      const shouldDelayCarousel = unlockState.delay > 0 || unlockState.waitForProgressBar;

      if (this.carouselRevealTimer) {
        clearTimeout(this.carouselRevealTimer);
        this.carouselRevealTimer = null;
      }

      if (!state.canAddFreeSample && !pickerOpen) {
        this.closePicker();
      }

      this.querySelectorAll('.js-sample-gift-add').forEach((btn) => {
        const shouldDisable = state.shouldDisableCarousel || !state.canAddFreeSample;
        btn.disabled = shouldDisable;
        if (!shouldDisable) {
          btn.removeAttribute('aria-disabled');
          btn.classList.remove('is-loading');
        }
      });

      const headingEl = this.querySelector('[data-sample-gift-heading]');
      const unlockEl = this.querySelector('[data-sample-gift-unlock-message]');
      if (shouldDelayCarousel) {
        if (headingEl) headingEl.hidden = true;
        if (unlockState.showMessage) {
          this.renderUnlockMessage(unlockEl);
        } else {
          this.restoreUnlockMessage(unlockEl);
        }
      } else {
        this.restoreUnlockMessage(unlockEl);
        this.restoreHeading(headingEl);
        if (headingEl) headingEl.hidden = !showCarousel;
      }

      const changeEl = this.querySelector('[data-sample-gift-change-wrap]');
      if (changeEl) changeEl.hidden = !showChange;

      const pickerEl = this.querySelector('[data-sample-gift-picker]');
      const pickerVisible = Boolean(pickerEl && !pickerEl.hidden);

      if (pickerEl && (!showCarousel || shouldDelayCarousel)) {
        if (!(pickerVisible && showCarousel && unlockSequence.wasRevealed())) {
          pickerEl.classList.remove('is-revealing');
          pickerEl.hidden = true;
        }
      }

      if (shouldDelayCarousel) {
        this.carouselRevealTimer = setTimeout(() => {
          this.carouselRevealTimer = null;
          this.syncUI(cart);
        }, Math.max(unlockState.delay, 50));
      } else if (showCarousel) {
        unlockSequence.markRevealed();
        this.revealPicker(pickerEl);
      }

      if (hadFreeApplied !== state.freeApplied) {
        this.dataset.freeApplied = state.freeApplied ? 'true' : 'false';
        if (cart && window.HugyCustomProgressBar?.refresh) {
          window.HugyCustomProgressBar.refresh(cart);
        }
        return;
      }

      this.dataset.freeApplied = state.freeApplied ? 'true' : 'false';
    }

    refreshDrawer() {
      document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    }

    async removeGiftLines(cart) {
      if (!cart?.items) return cart;
      const giftItems = cart.items.filter((item) => isGiftLine(item));
      if (!giftItems.length) return cart;

      for (const item of giftItems.sort((a, b) => (b.line || 0) - (a.line || 0))) {
        const line = item.line || cart.items.indexOf(item) + 1;
        const res = await fetch(this.cartChangeUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            line,
            quantity: 0,
            sections: this.getSectionsParam(),
            sections_url: window.location.pathname
          })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || res.status);
      }

      return this.fetchCart();
    }

    async onCartChange(cart) {
      if (this.isProcessing) return;

      try {
        this.isProcessing = true;
        const currentCart = cart?.items ? cart : await this.fetchCart();
        this.syncUI(currentCart);
        this.hideNotice();
      } catch (err) {
        console.error('cart-sample-gift:', err);
      } finally {
        this.isProcessing = false;
      }
    }

    async handleChangeClick() {
      if (this.isProcessing) return;

      this.isProcessing = true;
      try {
        const cart = await this.fetchCart();
        await this.removeGiftLines(cart);
        sessionStorage.setItem(PICKER_OPEN_KEY, '1');
        this.refreshDrawer();
      } catch (err) {
        console.error('cart-sample-gift change:', err);
        this.showNotice(window.theme?.strings?.sampleGiftError || '');
      } finally {
        this.isProcessing = false;
      }
    }

    async handleAddClick(btn) {
      if (btn.disabled || this.isProcessing) return;

      const variantId = btn.dataset.variantId;
      if (!variantId) return;

      this.isProcessing = true;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('is-loading');

      try {
        const cart = await this.fetchCart();
        const state = this.getState(cart);

        if (!state?.canAddFreeSample) {
          this.showNotice(window.theme?.strings?.sampleGiftLocked || '');
          return;
        }

        const res = await fetch(this.cartAddUrl(), {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            items: [{
              id: Number(variantId),
              quantity: 1,
              properties: { _sample_gift: 'true' }
            }],
            sections: this.getSectionsParam(),
            sections_url: window.location.pathname
          })
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || res.status);

        this.hideNotice();
        this.closePicker();

        const cartDrawer = document.getElementById('cart-drawer');
        if (cartDrawer?.renderContents && data.sections) {
          await cartDrawer.renderContents(data, false);
        } else {
          this.refreshDrawer();
        }

        document.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { cart: data }
        }));
      } catch (err) {
        console.error('cart-sample-gift add:', err);
        this.showNotice(window.theme?.strings?.sampleGiftError || '');
      } finally {
        this.isProcessing = false;
        btn.classList.remove('is-loading');
      }
    }
  }

  customElements.define('cart-drawer-sample-gift', CartDrawerSampleGift);
}
